import React, { useState, useEffect, useRef } from 'react';
import ConfirmModal from '@/components/ConfirmModal';
import Toast from '@/components/Toast';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { articleAdminApi } from '@/features/admin/articleAdminApi';
import { useAuth } from '@/contexts/AuthContext';

export const TranslationReview: React.FC = () => {
  const { user } = useAuth();
  const { country, id: articleId } = useParams<{ country: string; id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [article, setArticle] = useState<any>(null);
  const [originalALines, setOriginalALines] = useState<string[]>([]);
  const [originalBLines, setOriginalBLines] = useState<string[]>([]);
  const [originalCLines, setOriginalCLines] = useState<string[]>([]);
  const [translatedALines, setTranslatedALines] = useState<string[]>([]);
  const [translatedBLines, setTranslatedBLines] = useState<string[]>([]);
  const [translatedCLines, setTranslatedCLines] = useState<string[]>([]);
  const [notification, setNotification] = useState<{
    message: string;
    type?: 'success' | 'error' | 'info';
  } | null>(null);
  const [showPublishConfirm, setShowApproveConfirm] = useState(false);
  const notifTimeout = useRef<number | null>(null);

  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'info' = 'info',
    duration = 3000
  ) => {
    setNotification({ message, type });
    if (notifTimeout.current) window.clearTimeout(notifTimeout.current);
    notifTimeout.current = window.setTimeout(() => setNotification(null), duration);
  };

  useEffect(() => {
    return () => {
      if (notifTimeout.current) window.clearTimeout(notifTimeout.current);
    };
  }, []);

  // Refs for auto-resizing textareas
  const origARefs = useRef<Array<HTMLTextAreaElement | null>>([]);
  const origBRefs = useRef<Array<HTMLTextAreaElement | null>>([]);
  const origCRefs = useRef<Array<HTMLTextAreaElement | null>>([]);
  const transARefs = useRef<Array<HTMLTextAreaElement | null>>([]);
  const transBRefs = useRef<Array<HTMLTextAreaElement | null>>([]);
  const transCRefs = useRef<Array<HTMLTextAreaElement | null>>([]);

  const getLevelLabel = (level: 'A' | 'B' | 'C'): string => {
    if (level === 'A') return 'Easy';
    if (level === 'B') return 'Medium';
    return 'Hard';
  };

  const adjustTextareaHeight = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    origARefs.current.forEach(adjustTextareaHeight);
    origBRefs.current.forEach(adjustTextareaHeight);
    origCRefs.current.forEach(adjustTextareaHeight);
    transARefs.current.forEach(adjustTextareaHeight);
    transBRefs.current.forEach(adjustTextareaHeight);
    transCRefs.current.forEach(adjustTextareaHeight);
  }, [
    originalALines,
    originalBLines,
    originalCLines,
    translatedALines,
    translatedBLines,
    translatedCLines,
  ]);

  const fetchArticleDetail = async (country: string, articleId: string) => {
    setIsLoading(true);
    try {
      const data = await articleAdminApi.getById(country, articleId);
      setArticle(data);
    } catch (error) {
      showNotification('Failed to fetch article details', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (articleId) {
      fetchArticleDetail(country || '', articleId);
    }
  }, [country, articleId]);

  // (handleChange removed - not used)

  const splitToLines = (text: string) => {
    if (!text) return [];
    // First split by paragraphs (multiple newlines or line breaks)
    const paragraphs = text
      .split(/\r?\n\r?\n|\r?\n/)
      .map(s => s.trim())
      .filter(Boolean);

    const lines: string[] = [];

    for (const para of paragraphs) {
      // Prefer native sentence segmentation for better handling of abbreviations and decimals.
      const intlAny = Intl as any;
      if (typeof intlAny !== 'undefined' && typeof intlAny.Segmenter === 'function') {
        const segmenter = new intlAny.Segmenter('en', { granularity: 'sentence' });
        const segments = (Array.from(segmenter.segment(para)) as Array<{ segment: string }>)
          .map(item => String(item.segment || '').trim())
          .filter(Boolean);
        if (segments.length > 0) {
          lines.push(...segments);
          continue;
        }
      }

      // Fallback for older environments.
      const matches = para.match(/[^.!?]+[.!?]*(?:\s+|$)/g);
      if (matches) {
        lines.push(...matches.map(s => s.trim()).filter(Boolean));
      } else if (para) {
        lines.push(para);
      }
    }

    return lines.length > 0 ? lines : [text];
  };

  useEffect(() => {
    if (!article) return;
    const oA =
      country && country?.toUpperCase() !== 'VI'
        ? splitToLines(String(article.textALevelEN || ''))
        : splitToLines(String(article.textALevelUS || ''));
    const oB =
      country && country?.toUpperCase() !== 'VI'
        ? splitToLines(String(article.textBLevelEN || ''))
        : splitToLines(String(article.textBLevelUS || ''));
    const oC =
      country && country?.toUpperCase() !== 'VI'
        ? splitToLines(String(article.textCLevelEN || ''))
        : splitToLines(String(article.textCLevelUS || ''));

    const tAraw = String(article.textALevelVI || article.textALevelVI || '');
    const tBraw = String(article.textBLevelVI || article.textBLevelVI || '');
    const tCraw = String(article.textCLevelVI || article.textCLevelVI || '');
    const tA = splitToLines(tAraw);
    const tB = splitToLines(tBraw);
    const tC = splitToLines(tCraw);

    setOriginalALines(oA);
    setOriginalBLines(oB);
    setOriginalCLines(oC);
    setTranslatedALines(tA.length ? tA : oA.map(() => ''));
    setTranslatedBLines(tB.length ? tB : oB.map(() => ''));
    setTranslatedCLines(tC.length ? tC : oC.map(() => ''));
  }, [article]);

  const handleTranslatedLineChange = (level: 'A' | 'B' | 'C', index: number, value: string) => {
    if (level === 'A') {
      setTranslatedALines(prev => {
        const copy = [...prev];
        copy[index] = value;
        return copy;
      });
    } else if (level === 'B') {
      setTranslatedBLines(prev => {
        const copy = [...prev];
        copy[index] = value;
        return copy;
      });
    } else {
      setTranslatedCLines(prev => {
        const copy = [...prev];
        copy[index] = value;
        return copy;
      });
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Prepare article copy so UI-only line breaks are not persisted
      const toSave = { ...(article || {}) };
      if (translatedALines.length > 0) {
        // Join with single space to avoid adding UI-only newlines
        toSave.textALevelVI = translatedALines.join(' ').trim();
      }
      if (translatedBLines.length > 0) {
        toSave.textBLevelVI = translatedBLines.join(' ').trim();
      }
      if (translatedCLines.length > 0) {
        toSave.textCLevelVI = translatedCLines.join(' ').trim();
      }
      await articleAdminApi.update(country!, articleId!, toSave);
      navigate('/editor/articles', {
        state: {
          notification: { message: 'Article saved successfully!', type: 'success' },
          activeTab: 'translation',
        },
      });
    } catch (error) {
      showNotification('Failed to save article', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = () => {
    setShowApproveConfirm(true);
  };

  const performApprove = async () => {
    if (!article) return;
    article.isTranslationReviewed = true;
    article.translationReviewedBy = user?.name || 'Unknown';
    handleSave();
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Toast notification={notification} onClose={() => setNotification(null)} />
      <ConfirmModal
        open={showPublishConfirm}
        title="Confirm Approve Translation"
        description="Are you sure you want to approve this article translation?"
        confirmText="Yes, approve"
        cancelText="Cancel"
        isLoading={isLoading}
        onConfirm={performApprove}
        onCancel={() => setShowApproveConfirm(false)}
      />

      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Review Translation</h1>
          <p className="text-gray-600 mt-2">Review and edit generated translations</p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/editor/articles', { state: { activeTab: 'translation' } })}
        >
          ← Back to Articles
        </Button>
      </div>

      <Card className="p-6">
        <form className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            {article?.isTranslationReviewed && (
              <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800">
                Reviewed
              </span>
            )}
            {!article?.isTranslationReviewed && (
              <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-800">
                Pending Review
              </span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <span>{article?.title ?? article?.titleUK ?? article?.titleUS ?? ''}</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content level A ({getLevelLabel('A')})
            </label>
            <div className="space-y-4">
              {originalALines.length === 0 && (
                <div className="text-sm text-gray-500">No content</div>
              )}
              {originalALines.map((orig, i) => (
                <div key={`a-line-${i}`} className="space-y-2">
                  <textarea
                    readOnly
                    ref={el => {
                      origARefs.current[i] = el;
                      if (el) adjustTextareaHeight(el);
                    }}
                    value={orig}
                    rows={1}
                    style={{ overflow: 'hidden' }}
                    className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md font-mono text-sm resize-none"
                  />
                  <textarea
                    ref={el => {
                      transARefs.current[i] = el;
                      if (el) adjustTextareaHeight(el);
                    }}
                    value={translatedALines[i] ?? ''}
                    onInput={e => adjustTextareaHeight(e.currentTarget)}
                    onChange={e => handleTranslatedLineChange('A', i, e.target.value)}
                    rows={1}
                    style={{ overflow: 'hidden' }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm resize-none"
                    disabled={isLoading}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content level B ({getLevelLabel('B')})
            </label>
            <div className="space-y-4">
              {originalBLines.length === 0 && (
                <div className="text-sm text-gray-500">No content</div>
              )}
              {originalBLines.map((orig, i) => (
                <div key={`b-line-${i}`} className="space-y-2">
                  <textarea
                    readOnly
                    ref={el => {
                      origBRefs.current[i] = el;
                      if (el) adjustTextareaHeight(el);
                    }}
                    value={orig}
                    rows={1}
                    style={{ overflow: 'hidden' }}
                    className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md font-mono text-sm resize-none"
                  />
                  <textarea
                    ref={el => {
                      transBRefs.current[i] = el;
                      if (el) adjustTextareaHeight(el);
                    }}
                    value={translatedBLines[i] ?? ''}
                    onInput={e => adjustTextareaHeight(e.currentTarget)}
                    onChange={e => handleTranslatedLineChange('B', i, e.target.value)}
                    rows={1}
                    style={{ overflow: 'hidden' }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm resize-none"
                    disabled={isLoading}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content level C ({getLevelLabel('C')})
            </label>
            <div className="space-y-4">
              {originalCLines.length === 0 && (
                <div className="text-sm text-gray-500">No content</div>
              )}
              {originalCLines.map((orig, i) => (
                <div key={`c-line-${i}`} className="space-y-2">
                  <textarea
                    readOnly
                    ref={el => {
                      origCRefs.current[i] = el;
                      if (el) adjustTextareaHeight(el);
                    }}
                    value={orig}
                    rows={1}
                    style={{ overflow: 'hidden' }}
                    className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md font-mono text-sm resize-none"
                  />
                  <textarea
                    ref={el => {
                      transCRefs.current[i] = el;
                      if (el) adjustTextareaHeight(el);
                    }}
                    value={translatedCLines[i] ?? ''}
                    onInput={e => adjustTextareaHeight(e.currentTarget)}
                    onChange={e => handleTranslatedLineChange('C', i, e.target.value)}
                    rows={1}
                    style={{ overflow: 'hidden' }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm resize-none"
                    disabled={isLoading}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-4 pt-4 border-t">
            <Button type="button" onClick={handleSave} disabled={isLoading} className="flex-1">
              {isLoading ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button
              type="button"
              onClick={handleApprove}
              disabled={isLoading}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              Approve Translation
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/editor/articles', { state: { activeTab: 'translation' } })}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
